import { Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { ModalSidebar } from '../../../../../components/ModalSidebar'
import useUserAccessTier from '../../../../../hooks/useUserAccessTier'
import {
  buildInitialValues,
  buildSystemUserPayload,
  getMessageAction,
  getValidationSchema,
} from '../../AddEditUserModal/utils'
import { useCompanyAccessOptions } from '../../SystemUserFields'
import usePanelTabs from '../usePanelTabs'
import EditSystemUserForm from './EditSystemUserForm/EditSystemUserForm'
import useSystemUserInfo from './useSystemUserInfo'
import useUpdateSystemUser from './useUpdateSystemUser'

/**
 * Edit panel opened from the System User tab. Cancel and the header back button return to the tab.
 * Import also opens it for people who are not system users yet: with `onSaveDraft`, nothing is loaded
 * from or saved to Augmentt, and the settings go back to the import list.
 */
function EditSystemUserPanel({
  user,
  title,
  hasManagePermission = false,
  permissionLevels,
  permissionEntities,
  allCustomers,
  customerCollections,
  isFetchingCustomerCollections = false,
  setCollectionToView,
  toBackdrop = false,
  onHide,
  onSaved = null,
  onSaveDraft = null,
}) {
  const intl = useIntl()
  const tabs = usePanelTabs()
  const { isSocAdminUser, isOrgAdminUser } = useUserAccessTier()
  const companyAccessOptions = useCompanyAccessOptions({ allCustomers, customerCollections })
  const { currentUserCollectionId, allCustomersIds, allNonNfrCustomersIds } = companyAccessOptions
  const isDraft = !!onSaveDraft
  const canEnterDetailsByHand = !isDraft && !user.microsoft365Linked
  const {
    userInfo: savedUserInfo,
    isLoading,
    hasLoadError,
  } = useSystemUserInfo({ organizationId: user.organization, userId: user.id, enabled: !isDraft })
  const { saveSystemUser, isSaving, hasSaveError } = useUpdateSystemUser({ userId: user.id, onSaved })

  // A draft holds its company access and permissions itself.
  const userInfo = useMemo(() => {
    if (!isDraft) return savedUserInfo
    return {
      customers: user.customers,
      permissions: { ...user.permissions, excludeNFRCompany: user.exclude_nfr_company },
    }
  }, [isDraft, savedUserInfo, user])

  const initialValues = useMemo(
    () =>
      buildInitialValues({
        editMode: true,
        isSocAdminUser,
        currentUserCollectionId,
        user,
        userInfo,
        permissionEntities,
        allCustomersIds,
        allNonNfrCustomersIds,
      }),
    [
      isSocAdminUser,
      currentUserCollectionId,
      user,
      userInfo,
      permissionEntities,
      allCustomersIds,
      allNonNfrCustomersIds,
    ],
  )

  const validationSchema = useMemo(
    () => getValidationSchema(intl, isDraft, canEnterDetailsByHand),
    [intl, isDraft, canEnterDetailsByHand],
  )

  const handleSubmit = useCallback(
    (values) => {
      const payload = buildSystemUserPayload(values, {
        hasOrganizationWideAccess: isSocAdminUser || isOrgAdminUser,
        currentUserCollectionId,
        messageAction: getMessageAction({
          editMode: true,
          initialLoginOptions: initialValues.loginOptions,
          loginOptions: values.loginOptions,
        }),
        includeHandEnteredDetails: canEnterDetailsByHand,
      })
      if (!isDraft) return saveSystemUser(payload)

      onSaveDraft(payload)
      onHide()
      return null
    },
    [
      isDraft,
      canEnterDetailsByHand,
      saveSystemUser,
      onSaveDraft,
      onHide,
      isSocAdminUser,
      isOrgAdminUser,
      currentUserCollectionId,
      initialValues.loginOptions,
    ],
  )

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <ModalSidebar
        show
        onHide={onHide}
        size="lg"
        title={title}
        isLoading={isLoading || isSaving}
        toBackdrop={toBackdrop}
        headerBackButtonAction={onHide}
      >
        <EditSystemUserForm
          tabs={tabs}
          isOwner={user.chargebee_id != null}
          canEnterDetailsByHand={canEnterDetailsByHand}
          hasManagePermission={hasManagePermission}
          // Imported people sign in with their Microsoft account.
          isLoginOptionsLocked={isDraft}
          isSaving={isSaving}
          hasError={hasLoadError || hasSaveError}
          canSave={!hasLoadError}
          companyAccessOptions={companyAccessOptions}
          savedCollectionName={user.collectionName}
          isFetchingCustomerCollections={isFetchingCustomerCollections}
          setCollectionToView={setCollectionToView}
          permissionLevels={permissionLevels}
          permissionEntities={permissionEntities}
        />
      </ModalSidebar>
    </Formik>
  )
}

EditSystemUserPanel.propTypes = {
  /** The system user, or with `onSaveDraft` the import's draft person, who has no id yet */
  user: PropTypes.shape({
    id: PropTypes.number,
    organization: PropTypes.number,
    chargebee_id: PropTypes.string,
    microsoft365Linked: PropTypes.bool,
    job_title: PropTypes.string,
    department: PropTypes.string,
    phone: PropTypes.string,
    office_location: PropTypes.string,
    collectionName: PropTypes.string,
    // A draft's own company access and permissions
    customers: PropTypes.arrayOf(PropTypes.number),
    permissions: PropTypes.objectOf(PropTypes.number),
    exclude_nfr_company: PropTypes.bool,
  }).isRequired,
  /** Panel title shared with the System User tab */
  title: PropTypes.node.isRequired,
  hasManagePermission: PropTypes.bool,
  permissionLevels: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  permissionEntities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, module_name: PropTypes.string }))
    .isRequired,
  allCustomers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  customerCollections: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  isFetchingCustomerCollections: PropTypes.bool,
  setCollectionToView: PropTypes.func.isRequired,
  toBackdrop: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  /** Called after a system user is saved */
  onSaved: PropTypes.func,
  /** Receives a draft's settings instead of saving them to Augmentt */
  onSaveDraft: PropTypes.func,
}

export default EditSystemUserPanel
